import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, where
} from 'firebase/firestore';
import {
  Box, Container, Typography, Button, Paper, TextField,
  IconButton, Alert, CircularProgress, Chip,
  Select, MenuItem, FormControl, InputLabel, Tabs, Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

const TAG_CATEGORIES = [
  { value: 'tools', label: 'IT och Verktyg' },
  { value: 'standards', label: 'Standarder och Regelverk' },
  { value: 'competencies', label: 'Kompetensområden' },
  { value: 'industries', label: 'Branscherfarenhet' },
  { value: 'personal_profile', label: 'Personliga egenskaper' },
];

export default function Tags() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [tags, setTags] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [newTag, setNewTag] = useState({ name: '', category: 'tools', level: 'global', scopeId: '' });

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    const [tagSnap, compSnap, deptSnap, suggSnap] = await Promise.all([
      getDocs(collection(db, 'tags')),
      getDocs(collection(db, 'companies')),
      getDocs(collection(db, 'departments')),
      getDocs(query(collection(db, 'tagSuggestions'), where('status', '==', 'pending'))),
    ]);
    setTags(tagSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setCompanies(compSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setDepartments(deptSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setSuggestions(suggSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  async function handleAddTag() {
    if (!newTag.name.trim()) return;
    await addDoc(collection(db, 'tags'), {
      name: newTag.name.trim(),
      category: newTag.category,
      level: newTag.level,
      scopeId: newTag.scopeId || '',
      createdBy: currentUser.uid,
      createdAt: new Date(),
      status: 'approved'
    });
    setSaved('Tagg tillagd!');
    setTimeout(() => setSaved(''), 2000);
    setNewTag({ name: '', category: 'tools', level: 'global', scopeId: '' });
    loadAll();
  }

  async function handleDeleteTag(id) {
    await deleteDoc(doc(db, 'tags', id));
    loadAll();
  }

  async function handleApproveSuggestion(suggestion) {
    await addDoc(collection(db, 'tags'), {
      name: suggestion.name,
      category: suggestion.category,
      level: suggestion.level || 'global',
      scopeId: suggestion.scopeId || '',
      createdBy: suggestion.suggestedBy,
      createdAt: new Date(),
      status: 'approved'
    });
    await updateDoc(doc(db, 'tagSuggestions', suggestion.id), { status: 'approved' });
    setSaved('Tagg godkänd!');
    setTimeout(() => setSaved(''), 2000);
    loadAll();
  }

  async function handleRejectSuggestion(id) {
    await updateDoc(doc(db, 'tagSuggestions', id), { status: 'rejected' });
    loadAll();
  }

  const globalTags = tags.filter(t => t.level === 'global');
  const companyTags = tags.filter(t => t.level === 'company');
  const deptTags = tags.filter(t => t.level === 'department');

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Box sx={{ backgroundColor: '#1a1a1a', color: 'white', py: 2, px: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton color="inherit" onClick={() => navigate('/admin')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight="bold">Tagghantering</Typography>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        {saved && <Alert severity="success" sx={{ mb: 2 }}>{saved}</Alert>}

        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}
          sx={{ mb: 3, bgcolor: 'white', borderRadius: 1 }}>
          <Tab label="Lägg till taggar" />
          <Tab label={'Förslag att granska (' + suggestions.length + ')'} />
          <Tab label="Alla taggar" />
        </Tabs>

        {/* FLIKEN: Lägg till taggar */}
        {activeTab === 0 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Lägg till ny tagg</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Taggnamn"
                value={newTag.name}
                onChange={e => setNewTag(prev => ({ ...prev, name: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') handleAddTag(); }}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Kategori</InputLabel>
                <Select value={newTag.category} label="Kategori"
                  onChange={e => setNewTag(prev => ({ ...prev, category: e.target.value }))}>
                  {TAG_CATEGORIES.map(c => (
                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Nivå</InputLabel>
                <Select value={newTag.level} label="Nivå"
                  onChange={e => setNewTag(prev => ({ ...prev, level: e.target.value, scopeId: '' }))}>
                  <MenuItem value="global">Global – gäller alla</MenuItem>
                  <MenuItem value="company">Företag</MenuItem>
                  <MenuItem value="department">Affärsområde</MenuItem>
                </Select>
              </FormControl>
              {newTag.level === 'company' && (
                <FormControl fullWidth>
                  <InputLabel>Företag</InputLabel>
                  <Select value={newTag.scopeId} label="Företag"
                    onChange={e => setNewTag(prev => ({ ...prev, scopeId: e.target.value }))}>
                    {companies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
              {newTag.level === 'department' && (
                <FormControl fullWidth>
                  <InputLabel>Affärsområde</InputLabel>
                  <Select value={newTag.scopeId} label="Affärsområde"
                    onChange={e => setNewTag(prev => ({ ...prev, scopeId: e.target.value }))}>
                    {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddTag}>
                Lägg till tagg
              </Button>
            </Box>
          </Paper>
        )}

        {/* FLIKEN: Förslag */}
        {activeTab === 1 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Förslag från medarbetare
            </Typography>
            {suggestions.length === 0 ? (
              <Alert severity="info">Inga förslag att granska just nu.</Alert>
            ) : (
              suggestions.map(s => (
                <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#f9f9f9', borderRadius: 1, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight="bold">{s.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {TAG_CATEGORIES.find(c => c.value === s.category)?.label} | Föreslagen av: {s.suggestedByEmail}
                    </Typography>
                  </Box>
                  <Button size="small" variant="contained" color="success" startIcon={<CheckIcon />}
                    onClick={() => handleApproveSuggestion(s)}>
                    Godkänn
                  </Button>
                  <Button size="small" variant="outlined" color="error"
                    onClick={() => handleRejectSuggestion(s.id)}>
                    Avvisa
                  </Button>
                </Box>
              ))
            )}
          </Paper>
        )}

        {/* FLIKEN: Alla taggar */}
        {activeTab === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>Globala taggar</Typography>
              {TAG_CATEGORIES.map(cat => {
                const catTags = globalTags.filter(t => t.category === cat.value);
                if (catTags.length === 0) return null;
                return (
                  <Box key={cat.value} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>{cat.label}</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {catTags.map(tag => (
                        <Chip key={tag.id} label={tag.name}
                          onDelete={() => handleDeleteTag(tag.id)}
                          deleteIcon={<DeleteIcon />}
                        />
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Paper>

            {companies.map(company => {
              const cTags = companyTags.filter(t => t.scopeId === company.id);
              if (cTags.length === 0) return null;
              return (
                <Paper key={company.id} sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>{company.name}</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {cTags.map(tag => (
                      <Chip key={tag.id} label={tag.name} color="primary" variant="outlined"
                        onDelete={() => handleDeleteTag(tag.id)} />
                    ))}
                  </Box>
                </Paper>
              );
            })}

            {departments.map(dept => {
              const dTags = deptTags.filter(t => t.scopeId === dept.id);
              if (dTags.length === 0) return null;
              return (
                <Paper key={dept.id} sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>{dept.name}</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {dTags.map(tag => (
                      <Chip key={tag.id} label={tag.name} color="secondary" variant="outlined"
                        onDelete={() => handleDeleteTag(tag.id)} />
                    ))}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Container>
    </Box>
  );
}